import React, { useEffect, useRef, useState } from "react";
import { api, formatValue } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Search, X, Plus, ArrowRightLeft, TrendingUp, TrendingDown, Trash2 } from "lucide-react";
import PlayerTag from "@/components/PlayerTag";

function PlayerPicker({ label, list, onAdd, onRemove }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/public/trade/search", { params: { q } }).then(r => setResults(r.data.players || [])).catch(() => setResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="flex-1 min-w-[200px]">
      <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2 min-h-[26px]">
        {list.map(p => (
          <span key={p.player_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-white/5 border border-[var(--border)]" data-testid={`rt-chip-${p.player_id}`}>
            {p.name}
            <button onClick={() => onRemove(p.player_id)} className="text-[var(--text-3)] hover:text-red-400"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
      <div className="relative" ref={boxRef}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-3)]" />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search player…" className="input-dark w-full !pl-10 text-sm" data-testid={`rt-search-${label}`} />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full card-bordered overflow-hidden max-h-56 overflow-y-auto">
            {results.map(p => (
              <button key={p.player_id} onClick={() => { onAdd(p); setQ(""); setResults([]); }} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-white/5 text-left" data-testid={`rt-add-${p.player_id}`}>
                <PlayerTag player={p} size={24} link={false} showPos />
                <Plus className="w-3.5 h-3.5 text-[var(--text-3)] shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const money = (n) => `${n >= 0 ? "+" : "-"}${formatValue(Math.abs(n))}`;

export default function RealizedTrades({ format = "1qb", leagues = [] }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [received, setReceived] = useState([]);
  const [gave, setGave] = useState([]);
  const [leagueId, setLeagueId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/ledger/trades").then(r => setData(r.data)).catch(() => setData({ trades: [], summary: {} }));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!received.length || !gave.length) { toast.error("Add players to both sides"); return; }
    setSaving(true);
    try {
      await api.post("/ledger/trades", {
        league_id: leagueId || null, format,
        received: received.map(p => p.player_id), gave: gave.map(p => p.player_id),
      });
      toast.success("Trade logged");
      setReceived([]); setGave([]); setOpen(false); load();
    } catch { toast.error("Couldn't log trade"); }
    finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/ledger/trades/${id}`); load(); };

  if (data === null) return <div className="card-bordered p-8 flex justify-center"><Loader2 className="w-5 h-5 spin text-[var(--accent)]" /></div>;
  const s = data.summary || {};

  return (
    <div data-testid="realized-trades">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[var(--accent)] text-xs tracking-widest font-mono flex items-center gap-2"><ArrowRightLeft className="w-3.5 h-3.5" />// REALIZED TRADE P&amp;L</div>
        <button onClick={() => setOpen(!open)} className="btn-secondary text-xs inline-flex items-center gap-1" data-testid="rt-toggle"><Plus className="w-3 h-3" />Log a trade</button>
      </div>

      {open && (
        <div className="card-bordered p-4 mb-4" data-testid="rt-form">
          <div className="flex flex-col sm:flex-row gap-4 mb-3">
            <PlayerPicker label="YOU RECEIVED" list={received} onAdd={p => setReceived([...received, p])} onRemove={id => setReceived(received.filter(x => x.player_id !== id))} />
            <PlayerPicker label="YOU GAVE" list={gave} onAdd={p => setGave([...gave, p])} onRemove={id => setGave(gave.filter(x => x.player_id !== id))} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {leagues.length > 0 && (
              <select className="input-dark text-sm py-1.5 w-auto" value={leagueId} onChange={e => setLeagueId(e.target.value)} data-testid="rt-league">
                <option value="">No league</option>
                {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
              </select>
            )}
            <button onClick={save} disabled={saving} className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="rt-save">
              {saving ? <Loader2 className="w-3 h-3 spin" /> : <Plus className="w-3 h-3" />}Save trade
            </button>
          </div>
        </div>
      )}

      {(data.trades || []).length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="rt-summary">
          <div className="card-bordered p-3"><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">TRADES</div><div className="font-mono text-2xl">{s.count}</div></div>
          <div className="card-bordered p-3"><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">NET P&amp;L (DME)</div><div className="font-mono text-2xl" style={{ color: s.total_pnl >= 0 ? "var(--accent)" : "var(--negative)" }}>{money(s.total_pnl || 0)}</div></div>
          <div className="card-bordered p-3"><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">WINNING</div><div className="font-mono text-2xl text-[var(--accent)]">{s.winning || 0}<span className="text-[var(--text-3)] text-sm">/{s.count}</span></div></div>
        </div>
      )}

      {(data.trades || []).length === 0 ? (
        <div className="card-bordered p-6 text-sm text-[var(--text-3)]" data-testid="rt-empty">No trades logged yet. Log a deal you made and track whether it ages in your favor — DME re-values both sides live.</div>
      ) : (
        <div className="space-y-2">
          {data.trades.map(t => {
            const up = t.pnl >= 0;
            const Icon = up ? TrendingUp : TrendingDown;
            const color = up ? "var(--accent)" : "var(--negative)";
            return (
              <div key={t.id} className="card-bordered p-4" data-testid={`rt-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 text-sm">
                      <span className="text-[var(--accent)] font-medium">{t.received.map(p => p.name).join(", ") || "—"}</span>
                      <ArrowRightLeft className="w-3.5 h-3.5 text-[var(--text-3)]" />
                      <span className="text-[var(--text-2)]">{t.gave.map(p => p.name).join(", ") || "—"}</span>
                    </div>
                    <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono mt-1">
                      {t.league_name ? `${t.league_name} · ` : ""}{t.date}
                    </div>
                  </div>
                  <button onClick={() => del(t.id)} className="text-[var(--text-3)] hover:text-red-400 shrink-0" data-testid={`rt-del-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                  <div><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">EDGE AT TRADE</div><div className="font-mono text-sm">{money(t.net_then)}</div></div>
                  <div><div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">EDGE TODAY</div><div className="font-mono text-sm">{money(t.net_now)}</div></div>
                  <div>
                    <div className="text-[9px] tracking-widest text-[var(--text-3)] font-mono">YOUR P&amp;L</div>
                    <div className="font-mono text-sm inline-flex items-center gap-1" style={{ color }}><Icon className="w-3.5 h-3.5" />{money(t.pnl)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
